import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiError } from "@/lib/api";
import { useRefresh } from "@/context/RefreshContext";
import { PageTitle, Panel, Loading, Empty, ErrorState } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", username: "", password: "", machine_ids: [], settlement_mode: "MANUAL", settlement_percent: 95 };

export default function Merchants() {
  const { bump } = useRefresh();
  const { data, loading, error, reload } = useApi("/admin/merchants");
  const { data: machines } = useApi("/admin/machines");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const openNew = () => { setForm(empty); setEditing({ new: true }); };
  const openEdit = (m) => { setForm({ name: m.name, username: m.username, password: "", machine_ids: m.machine_ids, settlement_mode: m.settlement_mode || "MANUAL", settlement_percent: m.settlement_percent ?? 95 }); setEditing(m); };

  const codeFor = (id) => (machines || []).find((m) => m.id === id)?.code || id;

  const save = async () => {
    try {
      if (editing.new) {
        if (!form.name || !form.username || !form.password) return toast.error("Name, username and password are required");
        await api.post("/admin/merchants", form);
      } else {
        const body = { name: form.name, machine_ids: form.machine_ids, settlement_mode: form.settlement_mode, settlement_percent: Number(form.settlement_percent) };
        if (form.password) body.password = form.password;
        await api.put(`/admin/merchants/${editing.id}`, body);
      }
      toast.success("Merchant saved"); setEditing(null); bump();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (m) => {
    try { await api.put(`/admin/merchants/${m.id}`, { status: m.status === "active" ? "disabled" : "active" });
      toast.success(`Merchant ${m.status === "active" ? "disabled" : "enabled"}`); bump(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageTitle title="Merchants" subtitle="Create merchant accounts and assign machines">
        <button data-testid="new-merchant-btn" onClick={openNew}
          className="flex items-center gap-2 rounded-sm bg-white text-black px-3 py-1.5 text-sm font-medium hover:bg-neutral-200 transition-colors">
          <Plus className="h-4 w-4" /> New Merchant
        </button>
      </PageTitle>

      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> :
        data.length === 0 ? <Empty label="No merchant accounts yet" /> : (
        <Panel title={`Merchants (${data.length})`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="merchants-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Username</th>
                  <th className="px-4 py-2 font-medium">Settlement</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Machines</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((m) => (
                  <tr key={m.id} className="border-b border-border/60" data-testid={`merchant-row-${m.username}`}>
                    <td className="px-4 py-2.5">{m.name}</td>
                    <td className="px-4 py-2.5 mono">{m.username}</td>
                    <td className="px-4 py-2.5"><span className="text-xs mono px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{m.settlement_mode || "MANUAL"}</span></td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs" style={{ color: m.status === "active" ? "#10B981" : "#EF4444" }}>
                        {m.status === "active" ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 mono text-xs text-muted-foreground">
                      {m.machine_ids.length ? m.machine_ids.map(codeFor).join(", ") : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(m)} data-testid={`edit-merchant-${m.username}`} className="rounded-sm border border-border px-2 py-1 hover:bg-[#1c1c1c] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => toggle(m)} data-testid={`toggle-merchant-${m.username}`} className="rounded-sm border border-border px-2 py-1 hover:bg-[#1c1c1c] transition-colors">
                          {m.status === "active" ? <PowerOff className="h-3.5 w-3.5 text-[#EF4444]" /> : <Power className="h-3.5 w-3.5 text-[#10B981]" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0f0f0f] border-border max-h-[85vh] overflow-y-auto" data-testid="merchant-dialog">
          <DialogHeader><DialogTitle className="font-head">{editing?.new ? "New Merchant" : "Edit Merchant"}</DialogTitle><DialogDescription className="sr-only">Merchant account and machine assignment form</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input label="Business Name" testid="merchant-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Input label="Username" testid="merchant-username" value={form.username} disabled={!editing?.new} onChange={(v) => setForm({ ...form, username: v })} />
            <Input label={editing?.new ? "Password" : "New Password (leave blank to keep)"} testid="merchant-password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Settlement Mode</label>
                <select data-testid="merchant-settlement-mode" value={form.settlement_mode}
                  onChange={(e) => setForm({ ...form, settlement_mode: e.target.value })}
                  className="mt-1 w-full rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white">
                  <option value="MANUAL">MANUAL (portal-calculated)</option>
                  <option value="ROUTE">ROUTE (Razorpay auto)</option>
                </select>
              </div>
              <Input label="Settlement %" testid="merchant-settlement-percent" type="number" value={form.settlement_percent}
                onChange={(v) => setForm({ ...form, settlement_percent: v })} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Assigned Machines</div>
              <div className="border border-border rounded-sm max-h-48 overflow-y-auto">
                {(machines || []).map((m) => (
                  <label key={m.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#161616] cursor-pointer">
                    <input type="checkbox" checked={form.machine_ids.includes(m.id)}
                      onChange={(e) => setForm((f) => ({ ...f, machine_ids: e.target.checked ? [...f.machine_ids, m.id] : f.machine_ids.filter((x) => x !== m.id) }))} />
                    <span className="mono text-sm">{m.code}</span><span className="text-sm text-muted-foreground">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-merchant-btn" className="rounded-sm bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <input data-testid={testid} type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white disabled:opacity-50" />
    </div>
  );
}
